import { logSafeErrorMessage } from "@smartmanager/shell-ui/shared/logging/safeError";

export type DesktopClientPlatform = "mac" | "mac-intel" | "windows" | "linux" | "unknown";
export type MobileClientPlatform = "ios" | "android" | null;

export type ClientPlatformInput = {
  userAgent?: string;
  platform?: string;
  maxTouchPoints?: number;
  architecture?: string;
};

export type ClientDownloadEnvironment = {
  desktopPlatform: DesktopClientPlatform;
  mobilePlatform: MobileClientPlatform;
  macArchitectureKnown: boolean;
};

export function classifyClientDownloadEnvironment({
  userAgent = "",
  platform = "",
  maxTouchPoints = 0,
  architecture = "",
}: ClientPlatformInput): ClientDownloadEnvironment {
  const ua = userAgent.toLowerCase();
  const normalizedPlatform = platform.toLowerCase();
  const normalizedArchitecture = architecture.toLowerCase();
  const isIPadDesktopMode = normalizedPlatform.includes("mac") && maxTouchPoints > 1;
  const isIos = /iphone|ipad|ipod/.test(ua) || isIPadDesktopMode;
  if (isIos) {
    return { desktopPlatform: "unknown", mobilePlatform: "ios", macArchitectureKnown: false };
  }
  if (/android/.test(ua)) {
    return { desktopPlatform: "unknown", mobilePlatform: "android", macArchitectureKnown: false };
  }
  if (/mobile|webos|windows phone/.test(ua)) {
    return { desktopPlatform: "unknown", mobilePlatform: null, macArchitectureKnown: false };
  }
  if (ua.includes("win") || normalizedPlatform.includes("win")) {
    return { desktopPlatform: "windows", mobilePlatform: null, macArchitectureKnown: true };
  }
  if (ua.includes("mac") || normalizedPlatform.includes("mac")) {
    if (normalizedArchitecture.includes("x86")) {
      return { desktopPlatform: "mac-intel", mobilePlatform: null, macArchitectureKnown: true };
    }
    if (normalizedArchitecture.includes("arm")) {
      return { desktopPlatform: "mac", mobilePlatform: null, macArchitectureKnown: true };
    }
    return { desktopPlatform: "mac", mobilePlatform: null, macArchitectureKnown: false };
  }
  if (ua.includes("linux") || normalizedPlatform.includes("linux")) {
    const linuxIdentity = `${ua} ${normalizedPlatform} ${normalizedArchitecture}`;
    const isX64Linux = /(?:x86[_-]?64|amd64|\bx64\b)/.test(linuxIdentity);
    return {
      desktopPlatform: isX64Linux ? "linux" : "unknown",
      mobilePlatform: null,
      macArchitectureKnown: isX64Linux,
    };
  }
  return { desktopPlatform: "unknown", mobilePlatform: null, macArchitectureKnown: false };
}

type NavigatorUserAgentData = {
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<{ architecture?: string }>;
};

function browserPlatformInput(): ClientPlatformInput {
  const userAgentData = (navigator as Navigator & { userAgentData?: NavigatorUserAgentData }).userAgentData;
  return {
    userAgent: navigator.userAgent,
    platform: userAgentData?.platform || navigator.platform || "",
    maxTouchPoints: navigator.maxTouchPoints || 0,
  };
}

/** This browser's environment from what it says at once (a Mac's CPU stays unknown). */
export function currentClientDownloadEnvironment(): ClientDownloadEnvironment {
  if (typeof navigator === "undefined") {
    return { desktopPlatform: "unknown", mobilePlatform: null, macArchitectureKnown: false };
  }
  return classifyClientDownloadEnvironment(browserPlatformInput());
}

/** This browser's environment, asking a Mac's browser for its CPU when it can tell. */
export async function detectClientDownloadEnvironment(): Promise<ClientDownloadEnvironment> {
  const userAgentData = (navigator as Navigator & { userAgentData?: NavigatorUserAgentData }).userAgentData;
  const input = browserPlatformInput();
  const initial = classifyClientDownloadEnvironment(input);
  if (initial.desktopPlatform !== "mac") return initial;

  if (userAgentData?.getHighEntropyValues) {
    try {
      const highEntropyValues = await userAgentData.getHighEntropyValues(["architecture"]);
      return classifyClientDownloadEnvironment({
        ...input,
        architecture: String(highEntropyValues?.architecture || ""),
      });
    } catch (error) {
      console.warn("Unable to detect Mac CPU architecture for download:", logSafeErrorMessage(error));
    }
  }

  return initial;
}
